import React, { useState, useEffect } from 'react';
import { Card, CardBody } from 'reactstrap';
import api from '~/services/api';

function DetailRow({ label, children }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <div
        style={{
          fontSize: '10px',
          color: '#888780',
          marginBottom: '2px',
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: '12px', color: '#3d3d3a' }}>{children}</div>
    </div>
  );
}

function TimelineItem({ color, text }) {
  return (
    <div
      className="d-flex align-items-start"
      style={{ gap: '6px', marginBottom: '6px' }}
    >
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: color,
          marginTop: 5,
          flexShrink: 0,
        }}
      />
      <div style={{ fontSize: '11px', color: '#5F5E5A' }}>{text}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const statusMap = {
    0: { label: 'Aberto', bg: '#F1EFE8', color: '#444441' },
    1: { label: 'Aguardando pagamento', bg: '#FAEEDA', color: '#633806' },
    2: { label: 'Pago', bg: '#E1F5EE', color: '#085041' },
    3: { label: 'Aguardando entrega', bg: '#E6F1FB', color: '#0C447C' },
    4: { label: 'Entregue', bg: '#EAF3DE', color: '#27500A' },
    6: { label: 'Cancelado', bg: '#FCEBEB', color: '#791F1F' },
    7: { label: 'Malinha entregue', bg: '#EEEDFE', color: '#3C3489' },
    9: { label: 'Malinha finalizada', bg: '#EAF3DE', color: '#27500A' },
  };

  const s = statusMap[status] || {
    label: `Status ${status}`,
    bg: '#F1EFE8',
    color: '#444441',
  };

  return (
    <span
      style={{
        fontSize: '11px',
        padding: '2px 8px',
        borderRadius: '10px',
        background: s.bg,
        color: s.color,
      }}
    >
      {s.label}
    </span>
  );
}

export default function DetailsPanel({ ticket }) {
  const [cartDetails, setCartDetails] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ticket?.cart_id || !ticket?.subscriber_schema) return;

    const loadDetails = async () => {
      setLoading(true);

      // Carregar detalhes do pedido
      try {
        const { data } = await api.get(
          `/support/whatsapp/cart/${ticket.cart_id}/details`,
          { params: { subscriber_schema: ticket.subscriber_schema } }
        );
        if (data.cart) {
          setCartDetails(data.cart);
        }
      } catch (err) {
        console.error('Erro ao carregar detalhes do pedido:', err);
      }

      // Carregar timeline do WhatsApp
      try {
        const { data } = await api.get(
          `/support/whatsapp/cart/${ticket.cart_id}/timeline`,
          { params: { subscriber_schema: ticket.subscriber_schema } }
        );
        if (data.events) {
          setTimeline(data.events);
        }
      } catch (err) {
        console.error('Erro ao carregar timeline:', err);
      }

      setLoading(false);
    };

    loadDetails();
  }, [ticket?.cart_id, ticket?.subscriber_schema]);

  if (loading) {
    return (
      <Card
        className="card new-layout mb-0 h-100"
        style={{ border: '0.5px solid #dee2e6' }}
      >
        <CardBody className="d-flex align-items-center justify-content-center">
          <div className="text-muted font-size-12">Carregando detalhes...</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card
      className="card new-layout mb-0 h-100 d-flex flex-column"
      style={{ border: '0.5px solid #dee2e6', overflow: 'hidden' }}
    >
      {/* Header */}
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '0.5px solid #dee2e6',
        }}
      >
        <div style={{ fontSize: '13px', fontWeight: 500 }}>
          Detalhes do atendimento
        </div>
      </div>

      {/* Conteúdo scrollável */}
      <CardBody style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {/* Subscriber */}
        {ticket.subscriber_name && (
          <DetailRow label="Subscriber">
            <div style={{ fontWeight: 500 }}>{ticket.subscriber_name}</div>
          </DetailRow>
        )}
        {/* Loja */}
        {(ticket.store_name || cartDetails?.store?.fantasy_name) && (
          <DetailRow label="Loja">
            <div style={{ fontWeight: 500 }}>
              {cartDetails?.store?.fantasy_name || ticket.store_name}
            </div>
            {cartDetails?.store?.address && (
              <div style={{ color: '#5F5E5A', fontSize: '11px' }}>
                {cartDetails.store.address.city} -{' '}
                {cartDetails.store.address.state}
              </div>
            )}
          </DetailRow>
        )}
        {/* Cliente */}
        <DetailRow label="Cliente">
          <div style={{ fontWeight: 500 }}>
            {ticket.customer_name || 'Não informado'}
          </div>
          <div style={{ color: '#5F5E5A', fontSize: '11px' }}>
            {ticket.customer_phone || ''}
          </div>
        </DetailRow>
        {/* Pedido */}
        <DetailRow label="Pedido">
          <div style={{ fontWeight: 500 }}>#{ticket.order_number || '—'}</div>
          {cartDetails && (
            <div style={{ color: '#5F5E5A', fontSize: '11px' }}>
              R${' '}
              {Number(cartDetails.amount || 0)
                .toFixed(2)
                .replace('.', ',')}
            </div>
          )}
        </DetailRow>
        {/* Status */}
        {cartDetails && (
          <DetailRow label="Status">
            <StatusBadge status={cartDetails.status} />
          </DetailRow>
        )}

        {/* Método de entrega */}
        {cartDetails?.delivery_method && (
          <DetailRow label="Entrega">
            <div style={{ fontWeight: 500 }}>{cartDetails.delivery_method}</div>
          </DetailRow>
        )}

        {/* Forma de pagamento */}
        {cartDetails?.payment_type !== undefined &&
          cartDetails?.payment_type !== 0 && (
            <DetailRow label="Pagamento">
              <div style={{ fontWeight: 500 }}>
                Tipo {cartDetails.payment_type}
              </div>
            </DetailRow>
          )}

        {/* Criado por */}
        {cartDetails?.created_by?.name && (
          <DetailRow label="Vendedor">
            <div style={{ fontWeight: 500 }}>{cartDetails.created_by.name}</div>
          </DetailRow>
        )}

        {/* Timeline WhatsApp */}
        {timeline.length > 0 && (
          <>
            <div
              style={{
                borderTop: '0.5px solid #dee2e6',
                paddingTop: '10px',
                marginTop: '8px',
                marginBottom: '8px',
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 500,
                  marginBottom: '8px',
                  color: '#3d3d3a',
                }}
              >
                Histórico de Eventos
              </div>
              {timeline.map(evt => {
                const colorMap = {
                  success: '#60b05d',
                  info: '#185FA5',
                  danger: '#dc3545',
                  warning: '#ffc107',
                  secondary: '#888780',
                };
                return (
                  <TimelineItem
                    key={`${evt.type}-${evt.timestamp}`}
                    color={colorMap[evt.color] || '#888780'}
                    text={evt.label}
                  />
                );
              })}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}
